import {
    ApiActivationStatus,
    ApiCompoundAccess,
    ApiInterfaceClient,
    ApiServiceProvider,
    ApiServiceProviderActivation,
    ApiServiceProviderPage,
    ApiTenant,
} from '../types/serviceProviderApi.types';
import {
    CompoundAccess,
    InterfaceClient,
    ServiceProvider,
    ServiceProviderAction,
    ServiceProviderActivation,
    ServiceProviderPage,
    Tenant,
} from '../../serviceProviders/serviceProvider.types';
import { parseOptionalStringDate, parseStringDate } from '../../sharedComponents/dateHelper';
import { mapToSupplier } from './activationMapper';
import { ActivationStatus } from '../../activation/activation.types';

export const mapToActivationStatus = (status: ApiActivationStatus): ActivationStatus => {
    switch (status) {
        case ApiActivationStatus.ACTIVE:
            return ActivationStatus.ACTIVE;
        case ApiActivationStatus.PENDING:
            return ActivationStatus.PENDING;
    }
};

export const mapToApiActivationStatus = (activationStatus: ActivationStatus): ApiActivationStatus => {
    switch (activationStatus) {
        case ActivationStatus.ACTIVE:
            return ApiActivationStatus.ACTIVE;
        case ActivationStatus.PENDING:
            return ApiActivationStatus.PENDING;
    }
};

export const mapToApiServiceProviderActivation = (
    serviceProviderActivation: ServiceProviderActivation,
): ApiServiceProviderActivation => {
    return {
        activated_at: serviceProviderActivation.activatedAt.toISOString(),
        activation_status: mapToApiActivationStatus(serviceProviderActivation.activationStatus),
        service_provider_identification: serviceProviderActivation.serviceProviderId,
        interface_access_granted: serviceProviderActivation.apiAccessGranted,
    };
};

export const mapToTenant = (apiTenant: ApiTenant): Tenant => {
    switch (apiTenant) {
        case ApiTenant.VW_PROD:
            return Tenant.VW_PROD;
        case ApiTenant.VW_QA:
            return Tenant.VW_QA;
        case ApiTenant.VW_TEST:
            return Tenant.VW_TEST;
        case ApiTenant.RIO_TEST:
            return Tenant.RIO_TEST;
        case ApiTenant.LSP_TEST:
            return Tenant.LSP_TEST;
    }
};

export const mapToCompoundAccess = (apiCompoundAccess: ApiCompoundAccess): CompoundAccess => {
    switch (apiCompoundAccess) {
        case ApiCompoundAccess.NONE:
            return CompoundAccess.NONE;
        case ApiCompoundAccess.READ_ONLY:
            return CompoundAccess.READ_ONLY;
        case ApiCompoundAccess.READ_WRITE:
            return CompoundAccess.READ_WRITE;
    }
};

export const mapToApiCompoundAccess = (compoundAccess: CompoundAccess): { access: ApiCompoundAccess } => {
    switch (compoundAccess) {
        case CompoundAccess.NONE:
            return { access: ApiCompoundAccess.NONE };
        case CompoundAccess.READ_ONLY:
            return { access: ApiCompoundAccess.READ_ONLY };
        case CompoundAccess.READ_WRITE:
            return { access: ApiCompoundAccess.READ_WRITE };
    }
};

const mapToInterfaceClient = (apiInterfaceClient: ApiInterfaceClient): InterfaceClient => ({
    clientId: apiInterfaceClient.client_id,
    enabled: apiInterfaceClient.enabled,
});

export const mapToServiceProvider = (apiServiceProvider: ApiServiceProvider): ServiceProvider => ({
    accountId: apiServiceProvider.account_id,
    supplier: mapToSupplier(apiServiceProvider.supplier),
    serviceProviderName: apiServiceProvider.service_provider_name,
    serviceProviderId: apiServiceProvider.service_provider_identification,
    duns: apiServiceProvider.duns,
    registrationDate: parseStringDate(apiServiceProvider.activation_requested_at),
    activationDate: parseOptionalStringDate(apiServiceProvider.activated_at),
    apiAccessRequested: apiServiceProvider.interface_access_requested,
    activationStatus: mapToActivationStatus(apiServiceProvider.activation_status),
    compoundAccess: mapToCompoundAccess(apiServiceProvider.compound_access),
    tenants: apiServiceProvider.tenants.map((tenant) => mapToTenant(tenant)),
    interfaceClient: apiServiceProvider.interface_client && mapToInterfaceClient(apiServiceProvider.interface_client),
});

const mapToServiceProviderActions = (apiActions: string[]): ServiceProviderAction[] =>
    apiActions
        .map((apiAction) => {
            switch (apiAction) {
                case 'request.accept':
                    return ServiceProviderAction.REQUEST_ACCEPT;
                case 'request.reject':
                    return ServiceProviderAction.REQUEST_REJECT;
                case 'client.create':
                    return ServiceProviderAction.CLIENT_CREATE;
                case 'change.client.status':
                    return ServiceProviderAction.CHANGE_CLIENT_STATUS;
                case 'client.delete':
                    return ServiceProviderAction.CLIENT_DELETE;
            }
        })
        .filter((action) => typeof action !== 'undefined');

export const mapToServiceProvidersPage = (apiServiceProviderPage: ApiServiceProviderPage): ServiceProviderPage => {
    const serviceProviders = apiServiceProviderPage.service_providers.map(
        (apiServiceProvider: ApiServiceProvider): ServiceProvider => mapToServiceProvider(apiServiceProvider),
    );

    const allowedActions = mapToServiceProviderActions(apiServiceProviderPage.allowed_actions);

    return { serviceProviders, allowedActions };
};
